import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BrandMark, SectionLabel } from "../components/Brand";
import AssignmentBrief from "../components/AssignmentBrief";
import { api, formatHMS, formatIST, isValidUrl } from "../lib/api";
import {
  AlertTriangle,
  CheckCircle2,
  Save,
  Loader2,
  Upload,
  FileText,
  ExternalLink,
  Lock,
  XCircle,
  BookOpen,
  Send,
} from "lucide-react";
import { toast } from "sonner";

const REQUIRED_TEXT_LABELS = {
  full_name: "Full name",
  branch: "Branch",
  year: "Year",
  email: "Email",
  phone: "Phone",
  ai_tools_used: "AI / tools used",
  time_spent: "Approximate time spent",
};
const REQUIRED_URL_LABELS = {
  docs_url: "Assignment Google Docs link",
  walkthrough_url: "Walkthrough / Explanation Video (Drive / Loom)",
};
const OPTIONAL_URL_LABELS = {
  sheet_url: "Google Sheet (Audit System)",
  linkedin_url: "LinkedIn profile",
  portfolio_url: "GitHub / portfolio",
};

export default function AssignmentPage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [errors, setErrors] = useState([]); // validation popup messages
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [tab, setTab] = useState("brief"); // brief | submit

  const [form, setForm] = useState({
    full_name: "",
    branch: "",
    year: "",
    email: "",
    phone: "",
    linkedin_url: "",
    portfolio_url: "",
    docs_url: "",
    sheet_url: "",
    video_url: "",
    walkthrough_url: "",
    ai_tools_used: "",
    time_spent: "",
    cv_attached: "yes",
    cv_file_path: "",
    cv_filename: "",
  });

  const fileRef = useRef(null);
  const lastDraftRef = useRef("");

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get(`/candidate/${candidateId}`);
      const { candidate, remaining_seconds, expired } = res.data;
      setCandidate(candidate);
      setRemaining(remaining_seconds);
      setExpired(expired);
      if (candidate?.submission_status === "submitted") {
        navigate(`/success/${candidateId}`);
        return;
      }
      const sub = candidate.submission || {};
      setForm((prev) => ({
        ...prev,
        full_name: sub.full_name || candidate.full_name || "",
        branch: sub.branch || candidate.branch || "",
        year: sub.year || candidate.year || "",
        email: sub.email || candidate.email || "",
        phone: sub.phone || candidate.phone || "",
        linkedin_url: sub.linkedin_url || "",
        portfolio_url: sub.portfolio_url || "",
        docs_url: sub.docs_url || "",
        sheet_url: sub.sheet_url || "",
        video_url: sub.video_url || "",
        walkthrough_url: sub.walkthrough_url || "",
        ai_tools_used: sub.ai_tools_used || "",
        time_spent: sub.time_spent || "",
        cv_attached: sub.cv_attached || "yes",
        cv_file_path: sub.cv_file_path || "",
        cv_filename: sub.cv_filename || "",
      }));
      setDraftSavedAt(candidate.draft_saved_at || null);
    } catch (e) {
      toast.error("Candidate session not found");
      navigate("/start");
    } finally {
      setLoading(false);
    }
  }, [candidateId, navigate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (loading || expired) return;
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          setExpired(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [loading, expired]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!expired) {
        api
          .get(`/candidate/${candidateId}`)
          .then((r) => {
            setRemaining(r.data.remaining_seconds);
            setExpired(r.data.expired);
          })
          .catch(() => {});
      }
    }, 60000);
    return () => clearInterval(id);
  }, [candidateId, expired]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveDraft = useCallback(
    async (silent = false) => {
      if (expired) return;
      try {
        await api.post(`/candidate/${candidateId}/draft`, form);
        const stamp = new Date().toISOString();
        setDraftSavedAt(stamp);
        lastDraftRef.current = JSON.stringify(form);
        if (!silent) toast.success("Draft saved");
      } catch (e) {
        if (!silent) toast.error("Draft save failed");
      }
    },
    [form, candidateId, expired]
  );

  useEffect(() => {
    if (loading || expired) return;
    const id = setInterval(() => {
      const snap = JSON.stringify(form);
      if (snap !== lastDraftRef.current) {
        saveDraft(true);
      }
    }, 30000);
    return () => clearInterval(id);
  }, [form, loading, expired, saveDraft]);

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/candidate/${candidateId}/upload-cv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        cv_file_path: res.data.storage_path,
        cv_filename: res.data.original_filename,
        cv_attached: "yes",
      }));
      toast.success("CV uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const collectErrors = () => {
    const errs = [];
    Object.entries(REQUIRED_TEXT_LABELS).forEach(([k, label]) => {
      if (!(form[k] || "").trim()) errs.push(`${label} is required.`);
    });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      errs.push("Email is not a valid email address.");
    }
    Object.entries(REQUIRED_URL_LABELS).forEach(([k, label]) => {
      const v = (form[k] || "").trim();
      if (!v) errs.push(`${label} is required.`);
      else if (!isValidUrl(v)) errs.push(`${label} must be a valid http(s) URL.`);
    });
    Object.entries(OPTIONAL_URL_LABELS).forEach(([k, label]) => {
      const v = (form[k] || "").trim();
      if (v && !isValidUrl(v)) errs.push(`${label} must be a valid http(s) URL (or leave blank).`);
    });
    if (!(form.cv_file_path || "").trim()) {
      errs.push("CV / Resume upload is required.");
    }
    return errs;
  };

  const onFinalSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/candidate/${candidateId}/submit`, form);
      toast.success("Submission received");
      navigate(`/success/${candidateId}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submission failed";
      setErrors([detail]);
      setShowFinalConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const triggerFinalSubmit = () => {
    const errs = collectErrors();
    if (errs.length) {
      setErrors(errs);
      // Switch to submit tab if user is on brief
      setTab("submit");
      return;
    }
    setShowFinalConfirm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  const critical = remaining > 0 && remaining <= 5 * 60;
  const warning = remaining > 5 * 60 && remaining <= 30 * 60;

  const timerClass = expired
    ? "text-red-300 bg-red-500/15 border-red-500/40"
    : critical
    ? "text-[#ff4950] bg-[var(--bh-accent-soft)] border-[var(--bh-accent-border)] bh-pulse"
    : warning
    ? "text-yellow-300 bg-yellow-500/10 border-yellow-500/30"
    : "text-white bg-white/5 border-white/15";

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="assignment-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark compact />
            <div className="leading-tight min-w-0 border-l border-white/15 pl-3 hidden sm:block">
              <div className="font-display font-bold text-sm truncate" data-testid="candidate-name-header">
                {candidate?.full_name || "Candidate"}
              </div>
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-zinc-400 truncate">
                {candidate?.candidate_code}
              </div>
            </div>
          </div>
          <div
            data-testid="countdown-timer"
            className={`font-mono text-lg sm:text-xl font-bold border px-3 sm:px-5 py-2 rounded-sm tabular-nums ${timerClass}`}
          >
            {expired ? "TIME EXPIRED" : formatHMS(remaining)}
          </div>
        </div>
        {!expired && warning && (
          <div className="bg-yellow-500/10 border-t border-yellow-500/20 text-yellow-200 text-xs sm:text-sm px-6 py-2 text-center">
            <AlertTriangle className="inline w-4 h-4 mr-2 -mt-0.5" />
            Less than 30 minutes left. Please submit soon.
          </div>
        )}
        {!expired && critical && (
          <div className="bg-[var(--bh-accent-soft)] border-t border-[var(--bh-accent-border)] text-[#ff5f66] text-xs sm:text-sm px-6 py-2 text-center font-semibold">
            <AlertTriangle className="inline w-4 h-4 mr-2 -mt-0.5" />
            Final 5 minutes. Submit your work now.
          </div>
        )}
        {expired && (
          <div className="bg-[var(--bh-accent-soft)] border-t border-[var(--bh-accent-border)] text-[#ff5f66] text-xs sm:text-sm px-6 py-2 text-center font-semibold">
            <Lock className="inline w-4 h-4 mr-2 -mt-0.5" />
            Submission window closed. Your 4.5-hour assignment window has ended.
          </div>
        )}

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 border-t border-white/5">
          <div className="flex gap-8">
            <button
              data-testid="tab-brief"
              onClick={() => setTab("brief")}
              className={`bh-tab flex items-center gap-2 ${tab === "brief" ? "active" : ""}`}
            >
              <BookOpen className="w-3.5 h-3.5" /> Assignment Brief
            </button>
            <button
              data-testid="tab-submit"
              onClick={() => setTab("submit")}
              className={`bh-tab flex items-center gap-2 ${tab === "submit" ? "active" : ""}`}
            >
              <Send className="w-3.5 h-3.5" /> Submit
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-10 lg:py-14">
        {tab === "brief" ? (
          <AssignmentBrief />
        ) : (
          <SubmissionFormSection
            form={form}
            set={set}
            expired={expired}
            uploading={uploading}
            handleCvUpload={handleCvUpload}
            fileRef={fileRef}
            saveDraft={saveDraft}
            triggerFinalSubmit={triggerFinalSubmit}
            submitting={submitting}
            draftSavedAt={draftSavedAt}
            candidate={candidate}
          />
        )}
      </main>

      {/* Errors modal */}
      {errors.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="errors-modal"
          onClick={() => setErrors([])}
        >
          <div
            className="bg-[#0e0e10] border border-[var(--bh-accent-border)] rounded-sm max-w-lg w-full p-6 sm:p-8 bh-shake"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-[var(--bh-accent-soft)] border border-[var(--bh-accent-border)] flex items-center justify-center shrink-0">
                <XCircle className="w-5 h-5 text-[#ff4950]" />
              </div>
              <div>
                <SectionLabel className="mb-1">Please fix the following</SectionLabel>
                <h3 className="font-display font-bold text-xl text-white">
                  {errors.length === 1 ? "1 issue" : `${errors.length} issues`} need your attention
                </h3>
              </div>
            </div>
            <ul className="space-y-2 mb-6 max-h-64 overflow-auto pr-1">
              {errors.map((er, i) => (
                <li
                  key={i}
                  className="text-sm text-zinc-100 flex gap-2 items-start border-l-2 border-[#ff4950] pl-3 py-1"
                >
                  <span>{er}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button
                data-testid="errors-modal-close"
                onClick={() => setErrors([])}
                className="bh-btn-primary"
              >
                Got it — let me fix this
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final confirm modal */}
      {showFinalConfirm && !expired && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="final-confirm-modal">
          <div className="bg-[#0e0e10] border border-white/15 rounded-sm max-w-lg w-full p-6 sm:p-8">
            <SectionLabel className="mb-3">Before you submit</SectionLabel>
            <h3 className="font-display font-bold text-2xl mb-4 text-white">Are your links public?</h3>
            <p className="text-sm text-zinc-200 mb-6 leading-relaxed">
              Please make sure your Google Doc, Sheet, and Video links are{" "}
              <span className="text-white font-semibold">publicly viewable</span> or accessible to Brandhero.
              Submissions cannot be edited after final submit.
            </p>
            <ul className="text-xs text-zinc-300 space-y-2 mb-6">
              <li className="flex gap-2"><ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#ff4950]" /> Docs: {form.docs_url || "—"}</li>
              <li className="flex gap-2"><ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#ff4950]" /> Sheet: {form.sheet_url || "(optional, skipped)"}</li>
              <li className="flex gap-2"><ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#ff4950]" /> Walkthrough: {form.walkthrough_url || "—"}</li>
            </ul>
            <div className="flex gap-3 justify-end">
              <button
                data-testid="confirm-cancel-btn"
                onClick={() => setShowFinalConfirm(false)}
                className="bh-btn-secondary"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-submit-btn"
                onClick={onFinalSubmit}
                disabled={submitting}
                className="bh-btn-primary"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, Submit Final
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Submission form (inner) -----
function SubmissionFormSection({
  form,
  set,
  expired,
  uploading,
  handleCvUpload,
  fileRef,
  saveDraft,
  triggerFinalSubmit,
  submitting,
  draftSavedAt,
}) {
  return (
    <section className="space-y-10" data-testid="submission-form">
      <header>
        <SectionLabel className="mb-2">Submit your assignment</SectionLabel>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white">
          Submission Form
        </h1>
        <p className="bh-help mt-3 max-w-2xl">
          Fill every field marked with <span className="text-[#ff4950] font-bold">*</span>.
          Save draft anytime — the system auto-saves every 30 seconds. CV upload
          is required before final submission.
        </p>
      </header>

      <div>
        <SectionLabel className="mb-3">Your details</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-6 mt-2">
          <FieldText label="Full name" required value={form.full_name} onChange={set("full_name")} disabled={expired} testid="sub-input-name" />
          <FieldText label="Email" required type="email" value={form.email} onChange={set("email")} disabled={expired} testid="sub-input-email" />
          <FieldText label="Phone" required value={form.phone} onChange={set("phone")} disabled={expired} testid="sub-input-phone" />
          <FieldText label="Branch" required value={form.branch} onChange={set("branch")} disabled={expired} testid="sub-input-branch" />
          <FieldText label="Year" required value={form.year} onChange={set("year")} disabled={expired} testid="sub-input-year" />
        </div>
      </div>

      <div>
        <SectionLabel className="mb-3">Your links</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-6 mt-2">
          <FieldText label="LinkedIn profile" optional value={form.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/..." disabled={expired} testid="sub-input-linkedin" />
          <FieldText label="GitHub / portfolio" optional value={form.portfolio_url} onChange={set("portfolio_url")} placeholder="https://..." disabled={expired} testid="sub-input-portfolio" />
          <FieldText label="Assignment Google Docs" required value={form.docs_url} onChange={set("docs_url")} placeholder="https://docs.google.com/document/d/..." disabled={expired} testid="sub-input-docs" />
          <FieldText label="Google Sheet (Audit System)" optional value={form.sheet_url} onChange={set("sheet_url")} placeholder="https://docs.google.com/spreadsheets/..." disabled={expired} testid="sub-input-sheet" />
          <FieldText label="Walkthrough / Explanation Video (Drive / Loom)" required value={form.walkthrough_url} onChange={set("walkthrough_url")} placeholder="https://drive.google.com/... or https://loom.com/..." disabled={expired} testid="sub-input-walkthrough" />
        </div>
      </div>

      <div>
        <SectionLabel className="mb-3">Process</SectionLabel>
        <div className="space-y-6 mt-2">
          <div>
            <label className="bh-label bh-label-required">AI / tools used</label>
            <textarea
              data-testid="sub-input-ai-tools"
              className="bh-input min-h-[120px] resize-y"
              rows={4}
              value={form.ai_tools_used}
              onChange={set("ai_tools_used")}
              placeholder="List every AI tool, prompt approach, and how you used it. Be specific — this is scored."
              disabled={expired}
            />
          </div>
          <FieldText label="Approximate time spent" required value={form.time_spent} onChange={set("time_spent")} placeholder="e.g. 3 hours 45 minutes" disabled={expired} testid="sub-input-time-spent" />
        </div>
      </div>

      <div>
        <SectionLabel className="mb-3">CV / Resume <span className="text-[#ff4950]">*</span></SectionLabel>
        <p className="bh-help mt-2 mb-4">
          Upload your CV / Resume. Required before final submission. Accepted:
          PDF, DOC, DOCX, PNG, JPG · max 10MB.
        </p>
        <div className="border border-dashed border-white/20 rounded-sm p-6">
          {form.cv_filename && form.cv_file_path ? (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-[#ff4950] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{form.cv_filename}</div>
                  <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[#ff4950] mt-1">
                    Uploaded
                  </div>
                </div>
              </div>
              <button
                type="button"
                data-testid="sub-cv-replace-btn"
                onClick={() => fileRef.current?.click()}
                disabled={expired || uploading}
                className="text-xs uppercase tracking-wider text-zinc-300 hover:text-white"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Replace"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="sub-cv-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={expired || uploading}
              className="w-full flex items-center justify-center gap-3 text-sm text-zinc-200 hover:text-white py-3"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload CV / Resume (required)
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={handleCvUpload}
            className="hidden"
            data-testid="sub-cv-file-input"
          />
        </div>
      </div>

      {/* Action bar */}
      <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          {draftSavedAt && (
            <>
              <CheckCircle2 className="w-4 h-4 text-[#ff4950]" />
              Draft auto-saved · {formatIST(draftSavedAt)} IST
            </>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            data-testid="save-draft-btn"
            type="button"
            onClick={() => saveDraft(false)}
            disabled={expired}
            className="bh-btn-secondary"
          >
            <Save className="w-4 h-4" />
            Save Draft
          </button>
          <button
            data-testid="final-submit-btn"
            type="button"
            onClick={triggerFinalSubmit}
            disabled={expired || submitting}
            className="bh-btn-primary"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Final Submit
          </button>
        </div>
      </div>
    </section>
  );
}

function FieldText({ label, required, optional, type = "text", value, onChange, placeholder, disabled, testid }) {
  return (
    <div>
      <label className={`bh-label ${required ? "bh-label-required" : ""}`}>
        {label}
        {optional && <span className="ml-2 normal-case tracking-normal text-zinc-500 text-[0.65rem]">(optional)</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        className="bh-input"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}
